import React, { useState, useEffect } from 'react';
import { debounce } from '../utils/helpers';

/**
 * SearchBox Component
 * Live search functionality for Ghost posts
 */
function SearchBox({ 
    placeholder = 'Search posts...',
    minChars = 2,
    debounceMs = 300 
}) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    // Debounced search function
    const performSearch = debounce(async (searchQuery) => {
        if (searchQuery.length < minChars) {
            setResults([]);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);

        try {
            // Simple client-side search through page content
            // For better results, use Ghost Content API or search service
            const searchResults = [];
            
            // Example: Search through visible post links on the page
            const postLinks = document.querySelectorAll('article a[href^="/"]');
            postLinks.forEach((link) => {
                const text = link.textContent.toLowerCase();
                if (text.includes(searchQuery.toLowerCase())) {
                    const title = link.textContent;
                    const url = link.href;
                    if (!searchResults.find(r => r.url === url)) {
                        searchResults.push({ title, url });
                    }
                }
            });

            setResults(searchResults.slice(0, 5)); // Limit to 5 results
            setIsOpen(true);
        } catch (error) {
            console.error('Search error:', error);
        } finally {
            setIsSearching(false);
        }
    }, debounceMs);

    useEffect(() => {
        performSearch(query);
    }, [query]);

    // Close results when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setIsOpen(false);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const containerStyle = {
        position: 'relative',
        maxWidth: '600px',
        margin: '0 auto'
    };

    const inputContainerStyle = {
        position: 'relative',
        display: 'flex',
        alignItems: 'center'
    };

    const inputStyle = {
        width: '100%',
        padding: '12px 40px 12px 16px',
        fontSize: '16px',
        border: '2px solid #e1e4e8',
        borderRadius: '24px',
        outline: 'none',
        transition: 'border-color 0.2s',
        backgroundColor: '#fff'
    };

    const iconStyle = {
        position: 'absolute',
        right: '16px',
        color: '#738a94',
        pointerEvents: 'none'
    };

    const resultsStyle = {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        marginTop: '8px',
        backgroundColor: '#fff',
        border: '1px solid #e1e4e8',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        maxHeight: '300px',
        overflowY: 'auto',
        zIndex: 1000
    };

    const resultItemStyle = {
        padding: '12px 16px',
        borderBottom: '1px solid #f4f4f4',
        cursor: 'pointer',
        transition: 'background-color 0.2s'
    };

    const noResultsStyle = {
        padding: '16px',
        textAlign: 'center',
        color: '#738a94'
    };

    return (
        <div style={containerStyle} onClick={(e) => e.stopPropagation()}>
            <div style={inputContainerStyle}>
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={(e) => {
                        query.length >= minChars && setIsOpen(true);
                        e.target.style.borderColor = '#15171A';
                    }}
                    onBlur={(e) => setTimeout(() => e.target.style.borderColor = '#e1e4e8', 200)}
                    placeholder={placeholder}
                    style={inputStyle}
                />
                <span style={iconStyle}>
                    {isSearching ? '⌛' : '🔍'}
                </span>
            </div>

            {isOpen && query.length >= minChars && (
                <div style={resultsStyle}>
                    {results.length > 0 ? (
                        results.map((result, index) => (
                            <a
                                key={index}
                                href={result.url}
                                style={{ 
                                    ...resultItemStyle,
                                    display: 'block',
                                    color: '#15171A',
                                    textDecoration: 'none'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f4f4f4'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                                {result.title}
                            </a>
                        ))
                    ) : (
                        <div style={noResultsStyle}>
                            {isSearching ? 'Searching...' : 'No results found'}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default SearchBox;
